import { useEffect, useState } from 'react';
import {
  listRouteEpisodes,
  listRouteTasks,
  type RouteEpisodeOption,
  type RouteFaxRequest,
  type RouteTaskList,
} from '../../services/faxRouting';
import {
  afterSuggestion,
  afterTypeChange,
  afterTyping,
  EMPTY_TASK_NAME,
  type TaskNameState,
} from './routeTaskName';

export const NEW_TASK = '__new__';

/**
 * Where on a patient's chart a fax would be filed.
 *
 * Starts on the current episode and on the task Roger suggests for the fax's
 * type, so the common case is one click. Any other task, or a new one of any
 * misc type, can be chosen instead.
 */
export function useRouteDestination(
  patientId: string | null,
  faxTypeId: string | null,
  onError: (message: string) => void
) {
  const [episodes, setEpisodes] = useState<RouteEpisodeOption[]>([]);
  const [episodeId, setEpisodeId] = useState<string>('');
  const [taskList, setTaskList] = useState<RouteTaskList | null>(null);
  const [destination, setDestination] = useState<string>(NEW_TASK);
  const [newType, setNewTypeState] = useState<string>('');
  const [taskName, setTaskName] = useState<TaskNameState>(EMPTY_TASK_NAME);
  const newName = taskName.name;
  const setNewName = (value: string) => setTaskName(afterTyping(value));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setEpisodes([]);
    setEpisodeId('');
    setTaskList(null);
    setTaskName(EMPTY_TASK_NAME);
    if (!patientId) return;
    let cancelled = false;
    setLoading(true);
    listRouteEpisodes(patientId)
      .then(found => {
        if (cancelled) return;
        setEpisodes(found);
        const start = found.find(e => e.is_current) ?? found[0];
        setEpisodeId(start?.id ?? '');
      })
      .catch(error => {
        if (cancelled) return;
        console.error('Failed to load episodes', error);
        onError("Could not load the patient's episodes.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [patientId, onError]);

  useEffect(() => {
    setTaskList(null);
    if (!episodeId) return;
    let cancelled = false;
    setLoading(true);
    listRouteTasks(episodeId, faxTypeId)
      .then(found => {
        if (cancelled) return;
        setTaskList(found);
        setDestination(found.suggestion.task_id ?? NEW_TASK);
        setNewTypeState(found.suggestion.misc_task_type);
        setTaskName(prev =>
          afterSuggestion(prev, found.suggestion.task_name ?? null)
        );
      })
      .catch(error => {
        if (cancelled) return;
        console.error('Failed to load episode tasks', error);
        onError("Could not load the episode's tasks.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [episodeId, faxTypeId, onError]);

  // A suggested name fits only the suggested type; see routeTaskName.
  const setNewType = (value: string) => {
    setNewTypeState(value);
    if (!taskList) return;
    const { misc_task_type, task_name } = taskList.suggestion;
    setTaskName(prev =>
      afterTypeChange(prev, value, misc_task_type, task_name ?? null)
    );
  };

  const chartLocked = !!taskList?.chart_locked;
  const canFile =
    !loading &&
    !chartLocked &&
    // Only a task list for the chosen episode makes the destination valid.
    !!taskList &&
    !!episodeId &&
    (destination !== NEW_TASK || !!newType);

  /** The destination half of a filing; the caller adds patient and type. */
  const request = (): Omit<RouteFaxRequest, 'patient_id' | 'fax_type_id'> => ({
    episode_id: episodeId,
    ...(destination === NEW_TASK
      ? {
          new_task: {
            misc_task_type: newType,
            task_name: newName.trim() || null,
          },
        }
      : { task_id: destination }),
  });

  return {
    episodes,
    episodeId,
    setEpisodeId,
    taskList,
    destination,
    setDestination,
    newType,
    setNewType,
    newName,
    setNewName,
    loading,
    chartLocked,
    canFile,
    request,
  };
}

export type RouteDestination = ReturnType<typeof useRouteDestination>;
